import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { globalAlert } from "../../actions/commonActions";
import { lmsUploadAssetApi } from "../../admin/_lmsActions";

const MAX_MB = { video: 500, pdf: 50, image: 20 };

const detectType = (file) => {
  const mime = String(file?.type || "").toLowerCase();
  const name = String(file?.name || "").toLowerCase();

  if (mime.startsWith("video/")) return "video";
  if (
    mime === "application/pdf" ||
    mime === "application/x-pdf" ||
    mime === "application/acrobat" ||
    name.endsWith(".pdf")
  ) {
    return "pdf";
  }
  if (mime.startsWith("image/")) return "image";
  return null;
};

const acceptAllowsFile = (accept = "", file) => {
  if (!accept) return true;
  const mime = String(file?.type || "").toLowerCase();
  const name = String(file?.name || "").toLowerCase();
  const acceptedTypes = accept
    .split(",")
    .map((type) => type.trim().toLowerCase())
    .filter(Boolean);

  return acceptedTypes.some((type) => {
    if (type.endsWith("/*")) {
      return mime.startsWith(type.replace("/*", "/"));
    }

    if (type.startsWith(".")) return name.endsWith(type);
    if (type === "application/pdf") return name.endsWith(".pdf") || mime === type;
    return mime === type;
  });
};

const fileInputAccept = (accept) => {
  const value =
    accept || "video/mp4,video/webm,video/quicktime,application/pdf,image/*";
  const parts = value
    .split(",")
    .map((type) => type.trim())
    .filter(Boolean);
  const hasPdfMime = parts.some((type) => type.toLowerCase() === "application/pdf");
  const hasPdfExt = parts.some((type) => type.toLowerCase() === ".pdf");
  return hasPdfMime && !hasPdfExt ? [...parts, ".pdf"].join(",") : value;
};

// Read a video's duration client-side so the asset carries durationSeconds
// (needed for watch-percentage completion) without a manual entry step.
const readVideoDuration = (file) =>
  new Promise((resolve) => {
    try {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => {
        URL.revokeObjectURL(v.src);
        resolve(Math.round(v.duration) || 0);
      };
      v.onerror = () => resolve(0);
      v.src = URL.createObjectURL(file);
    } catch {
      resolve(0);
    }
  });

// One-click uploader: pick a file → POST /assets/upload → onUploaded(asset).
// The returned asset (with a transient previewUrl) is handed to the caller to
// attach via the relevant granular route.
const LmsAssetUpload = ({
  onUploaded,
  accept,
  label,
  uploadTitle,
  hasVoiceOver = false,
  maxMb,
  iconClass = "fas fa-upload",
  uploadContext = {},
}) => {
  const { t } = useTranslation();
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (ref.current) ref.current.value = "";
    if (!file) return;

    if (!acceptAllowsFile(accept, file)) {
      globalAlert("error", t("admin.lms.asset.unsupported", { types: accept }));
      return;
    }

    const type = detectType(file);
    if (!type) {
      globalAlert("error", t("admin.lms.asset.unsupported"));
      return;
    }
    const sizeMb = file.size / (1024 * 1024);
    const maxSizeMb = maxMb || MAX_MB[type];
    if (sizeMb > maxSizeMb) {
      globalAlert("error", t("admin.lms.asset.tooLarge", { max: maxSizeMb }));
      return;
    }
    setBusy(true);
    setProgress(0);
    try {
      const title = uploadTitle?.trim?.() || file.name.replace(/\.[^.]+$/, "");
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      Object.entries(uploadContext || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          fd.append(key, String(value));
        }
      });
      if (hasVoiceOver) fd.append("hasVoiceOver", "true");
      if (type === "video") {
        const dur = await readVideoDuration(file);
        if (dur) fd.append("durationSeconds", String(dur));
      }
      const res = await lmsUploadAssetApi(fd, {
        onUploadProgress: (event) => {
          if (!event.total) return;
          setProgress(Math.min(99, Math.round((event.loaded * 100) / event.total)));
        },
      });
      if (res?.data?.status === 200) {
        setProgress(100);
        const asset = res?.data?.data?.asset || {};
        const previewUrl = res?.data?.data?.previewUrl;
        await onUploaded?.({ ...asset, title: asset.title || title, previewUrl });
      } else {
        globalAlert("error", res?.data?.message);
      }
    } catch (err) {
      globalAlert(
        "error",
        err?.response?.data?.message || t("admin.lms.asset.uploadFailed")
      );
    }
    setBusy(false);
    setTimeout(() => setProgress(0), 600);
  };

  return (
    <>
      <input
        type="file"
        ref={ref}
        accept={fileInputAccept(accept)}
        style={{ display: "none" }}
        onChange={onFile}
      />
      <button
        type="button"
        className="btn btn-sm secondary-btn lms-asset-upload-btn btn-with-icon"
        onClick={() => ref.current?.click()}
        disabled={busy}
      >
        {busy ? (
          <>
            <i className="fas fa-circle-notch fa-spin mr-1"></i>
            {progress > 0
              ? `${t("admin.lms.asset.uploading")} ${progress}%`
              : t("admin.lms.asset.uploading")}
          </>
        ) : (
          <>
            <i className={`${iconClass} mr-1`}></i>
            {label || t("admin.lms.asset.upload")}
          </>
        )}
      </button>
      {busy && (
        <div className="lms-upload-progress" aria-label="Upload progress">
          <span style={{ width: `${progress}%` }}></span>
        </div>
      )}
    </>
  );
};

export default LmsAssetUpload;
